import { useEffect, useId, useRef, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { useDialogA11y } from '../../lib/useDialogA11y';
import { CloseIcon } from '../icons';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  phonePreview?: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg' | 'xl' | '2xl' | 'full';
  className?: string;
}

const SIZES = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  '2xl': 'max-w-5xl',
  full: 'max-w-full',
};

export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  phonePreview,
  footer,
  size = phonePreview ? '2xl' : 'xl',
  className,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(open, panelRef, onClose);

  useEffect(() => {
    if (!open) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex overflow-hidden">
      {/* Backdrop */}
      <button
        type="button"
        tabIndex={-1}
        aria-label="Close drawer"
        onClick={onClose}
        className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity"
      />

      {/* Slide-over panel (positioned at end: right in LTR, left in RTL) */}
      <div className="fixed inset-y-0 end-0 flex max-w-full">
        <div
          className={cn(
            'flex w-screen flex-col border-s border-line bg-white shadow-2xl transition-all',
            'dark:border-slate-800 dark:bg-slate-900',
            SIZES[size],
            className,
          )}
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-line px-6 py-4 dark:border-slate-800">
            <div className="min-w-0 flex-1 pe-4">
              {title && (
                <h2 id={titleId} className="text-lg font-bold text-ink dark:text-slate-100">{title}</h2>
              )}
              {subtitle && (
                <p className="mt-0.5 text-xs text-ink-3 dark:text-slate-400">{subtitle}</p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="grid h-8 w-8 place-items-center rounded-lg text-ink-3 hover:bg-slate-100 hover:text-ink dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
              aria-label="Close"
            >
              <CloseIcon size={18} />
            </button>
          </div>

          {/* Body with optional phone preview slot */}
          <div className="flex min-h-0 flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6">{children}</div>

            {phonePreview && (
              <div className="hidden w-[380px] shrink-0 flex-col items-center justify-center border-s border-line bg-slate-50/70 p-6 lg:flex dark:border-slate-800 dark:bg-slate-950/40">
                <div className="w-full max-w-[320px]">{phonePreview}</div>
              </div>
            )}
          </div>

          {/* Footer */}
          {footer && (
            <div className="border-t border-line px-6 py-4 dark:border-slate-800">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Drawer;
